// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import webMatchPredictionApi from './webMatchPrediction.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchWebMatchPredictionApi = createAsyncThunk(
    'shopSaga/webMatchPrediction/fetchWebMatchPredictionApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await webMatchPredictionApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const createWebMatchPredictionApi = createAsyncThunk(
    'shopSaga/webMatchPrediction/createWebMatchPredictionApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await webMatchPredictionApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeWebMatchPredictionApi = createAsyncThunk(
    'shopSaga/webMatchPrediction/storeWebMatchPredictionApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const webMatchPrediction = await webMatchPredictionApi.store(values);
            alertSwal('success', 'WebMatchPrediction created successfully!');

            return webMatchPrediction;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showWebMatchPredictionApi = createAsyncThunk(
    'shopSaga/webMatchPrediction/showWebMatchPredictionApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await webMatchPredictionApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateWebMatchPredictionApi = createAsyncThunk(
    'shopSaga/webMatchPrediction/updateWebMatchPredictionApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await webMatchPredictionApi.update(id, values);
            alertSwal('success', 'WebMatchPrediction updated successfully!');

            const { ranks } = state.shopSaga.webMatchPredictionMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteWebMatchPredictionApi = createAsyncThunk('shopSaga/webMatchPrediction/deleteWebMatchPredictionApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await webMatchPredictionApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const webMatchPredictionSlice = createSlice({
    name: 'shopSaga/webMatchPrediction',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedWebMatchPrediction(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWebMatchPredictionApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchWebMatchPredictionApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(createWebMatchPredictionApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createWebMatchPredictionApi.rejected, () => {
                //
            });

        builder
            .addCase(storeWebMatchPredictionApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeWebMatchPredictionApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showWebMatchPredictionApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showWebMatchPredictionApi.rejected, () => {
                //
            });

        builder
            .addCase(updateWebMatchPredictionApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateWebMatchPredictionApi.rejected, () => {
                //
            });

        builder.addCase(deleteWebMatchPredictionApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedWebMatchPrediction } = webMatchPredictionSlice.actions;
